import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import { writeException as warn } from '@/utils/logger';
import storage from '@/utils/storage';

import { HTTP_CODE, TOKENINVALIDCODES } from './http';
import { reportException } from './sentry';

export function authResponseSuccessInterceptor(response: AxiosResponse) {
  return response.data; // return backend created struct: APIResponse
}

/**
 * Backend sometimes leaks raw low-level DB errors (e.g. MySQL duplicate key
 * errors like "Database error: 1062 (23000): Duplicate entry '...' for key
 * '...'"). Replace those with a friendly message instead of showing users
 * the raw error.
 * @date 2026/09/18
 * @param rawMessage message coming from the backend response
 * @returns a user-friendly message
 */
function toFriendlyErrorMessage(rawMessage?: string) {
  if (!rawMessage) return rawMessage;
  if (/duplicate entry/i.test(rawMessage)) {
    return 'This value already exists. Please use a different one and try again.';
  }
  if (/database error/i.test(rawMessage)) {
    return 'Something went wrong while saving. Please try again.';
  }
  return rawMessage;
}

/**
 * Axios instance response interceptor to handle system errors
 * @date 2024/11/05
 * @param error error object
 * @returns Promise
 */
export function authResponseFailureInterceptor(error: any) {
  const code = error.response?.status as number;
  warn('axios', `### got response code: ${code} ###`);

  const errorDetail = error.response?.data?.detail; // this is what our system return
  const errorMessage = error.response?.data?.message; // or use message property
  const defaultError = error.message;

  const rawErrorMessage = errorDetail || errorMessage || defaultError;
  const message = toFriendlyErrorMessage(rawErrorMessage);

  // callers with their own error UI (e.g. the save queue) opt out via
  // `silentError` so the failure isn't toasted/reported twice
  // @2026/09/22
  const silentError = !!error.config?.silentError;

  if (!silentError) {
    toast.error(message);
  }

  // create a new error to report!
  const errObj = new Error(message);
  // global backend error broadcasting
  // @2026/09/22
  if (!silentError) {
    reportException(new Error(rawErrorMessage));
  }

  const isTokenInvalid = TOKENINVALIDCODES.includes(code);
  if (isTokenInvalid) {
    warn('axios', '## got auth error to clear token ....');
    isTokenInvalid && storage.clearToken();
    // === decouple error handling the logic from low level tool
    // @2023/07/22
    const detail = HTTP_CODE[code];
    const eventType = detail.event;
    // FIXME: send error detail - @2024/11/05
    // to further discover the error!
    document.dispatchEvent(new CustomEvent(eventType, { detail }));
    // return error on token failure
    warn('axios', `## to return the error of code: ${code} `);
    return Promise.reject(error);
  }

  // FIXME: throw error object finally! - @2024/09/16
  return Promise.reject(errObj);
}
